import { useState } from 'react';
import type { CallRatings } from '@klaro7/shared-types';
import { Button } from '@klaro7/ui';

import { useCall } from '../context/CallContext';
import { useRateCall } from '../hooks/useCallHistory';

type RatingKey = keyof CallRatings;

const RATING_FIELDS: { key: RatingKey; label: string }[] = [
  { key: 'overall', label: 'Voto complessivo' },
  { key: 'professionalism', label: 'Professionalità' },
  { key: 'clarity', label: 'Chiarezza' },
  { key: 'punctuality', label: 'Puntualità' },
  { key: 'availability', label: 'Disponibilità' },
];

const EMPTY_RATINGS: CallRatings = {
  overall: 0,
  professionalism: 0,
  clarity: 0,
  punctuality: 0,
  availability: 0,
};

function StarRow({
  label,
  value,
  onChange,
}: {
  label: string;
  value: number;
  onChange: (n: number) => void;
}) {
  const [hovered, setHovered] = useState(0);
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm text-neutral-700">{label}</span>
      <div className="flex gap-1" onMouseLeave={() => setHovered(0)}>
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            onClick={() => onChange(star)}
            onMouseEnter={() => setHovered(star)}
            className="p-0.5"
            aria-label={`${label}: ${star} stelle`}
          >
            <svg
              className={`h-6 w-6 ${
                star <= (hovered || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-neutral-300'
              }`}
              viewBox="0 0 24 24"
              stroke="currentColor"
              strokeWidth={1}
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.563.563 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.563.563 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
            </svg>
          </button>
        ))}
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  if (m === 0) return `${s}s`;
  return `${m}m ${s}s`;
}

const endReasonLabels: Record<string, string> = {
  caller_hangup: 'Hai terminato la chiamata',
  callee_hangup: 'Il consulente ha terminato la chiamata',
  caller_disconnect: 'Connessione persa (utente)',
  callee_disconnect: 'Connessione persa (consulente)',
  insufficient_credits: 'Credito esaurito',
  timeout: 'Nessuna risposta',
  max_duration: 'Durata massima raggiunta',
  manual_pause_timeout: 'Terminata dopo pausa non ripresa',
  error: 'Errore di sistema',
};

export function CallEndSummary() {
  const { callEndInfo, dismissCallEnd } = useCall();
  const rateCallMutation = useRateCall();
  const [ratings, setRatings] = useState<CallRatings>(EMPTY_RATINGS);
  const [feedback, setFeedback] = useState('');
  const [rated, setRated] = useState(false);

  if (!callEndInfo) return null;

  const allRated = (Object.values(ratings) as number[]).every((v) => v > 0);

  const updateRating = (key: RatingKey, value: number) => {
    setRatings((prev: CallRatings) => ({ ...prev, [key]: value }));
  };

  const handleRate = async () => {
    if (!allRated) return;
    try {
      await rateCallMutation.mutateAsync({
        id: callEndInfo.callSessionId,
        data: { ratings, feedback: feedback || undefined },
      });
      setRated(true);
    } catch {
      // Rating failed - ignore
    }
  };

  return (
    <div className="fixed inset-0 z-[95] flex items-center justify-center bg-black/50 backdrop-blur-sm">
      <div className="mx-4 w-full max-w-sm rounded-2xl bg-white p-6 shadow-2xl">
        <div className="text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-brand-teal-50">
            <svg className="h-8 w-8 text-brand-teal" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
            </svg>
          </div>

          <h3 className="text-lg font-bold text-neutral-900">Chiamata terminata</h3>
          <p className="mt-1 text-sm text-neutral-600">
            {endReasonLabels[callEndInfo.endReason] || 'Chiamata terminata'}
          </p>
        </div>

        <div className="mt-5 flex justify-center gap-8 text-center">
          <div>
            <p className="text-2xl font-bold text-neutral-900">{formatDuration(callEndInfo.durationSeconds)}</p>
            <p className="text-xs text-neutral-600">Durata</p>
          </div>
          <div>
            {callEndInfo.proEarning !== undefined ? (
              <>
                <p className="text-2xl font-bold text-neutral-900">{(callEndInfo.proEarning ?? 0).toFixed(2)} €</p>
                <p className="text-xs text-neutral-600">Guadagno</p>
              </>
            ) : (
              <>
                <p className="text-2xl font-bold text-neutral-900">{callEndInfo.totalCost.toFixed(2)} €</p>
                <p className="text-xs text-neutral-600">Costo</p>
              </>
            )}
          </div>
        </div>

        {/* Rating */}
        {!rated ? (
          <div className="mt-6">
            <p className="mb-3 text-center text-sm font-medium text-neutral-700">
              Come valuti la consulenza?
            </p>
            <div className="space-y-2">
              {RATING_FIELDS.map((field) => (
                <StarRow
                  key={field.key}
                  label={field.label}
                  value={ratings[field.key]}
                  onChange={(n) => updateRating(field.key, n)}
                />
              ))}
            </div>
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder="Lascia un commento (opzionale)"
              className="mt-4 w-full rounded-lg border border-neutral-200 p-2 text-sm focus:border-brand-teal focus:outline-none focus:ring-1 focus:ring-brand-teal"
              rows={2}
            />
            <Button
              onClick={handleRate}
              loading={rateCallMutation.isPending}
              disabled={!allRated}
              className="mt-3 w-full"
            >
              Invia valutazione
            </Button>
            {!allRated && (
              <p className="mt-2 text-center text-xs text-neutral-500">
                Assegna almeno una stella a ogni criterio
              </p>
            )}
          </div>
        ) : (
          <p className="mt-4 text-center text-sm text-green-600">Grazie per la tua valutazione!</p>
        )}

        <button
          onClick={dismissCallEnd}
          className="mt-4 w-full text-center text-sm text-neutral-500 hover:text-neutral-700"
        >
          Chiudi
        </button>
      </div>
    </div>
  );
}
